import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, StatusPill } from "../components/primitives";
import jsPDF from "jspdf";
import { useAuth } from "../lib/auth";

export default function ExaminationReadiness() {
  const [data, setData] = useState(null);
  const { institution } = useAuth();
  useEffect(() => { api.get("/governance/readiness").then(r => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-[13px] text-ink-muted">Loading…</div>;

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`${institution?.name || 'Acme Demo Bank Plc'} — Examination Evidence Package`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Generated ${new Date().toLocaleString()} · CBN Directive 10 March 2026 alignment`, 14, 25);
    doc.line(14, 29, 196, 29);
    doc.setFontSize(12);
    doc.text(`Composite readiness score: ${data.score}/100`, 14, 40);
    doc.text("Directive section coverage:", 14, 50);
    let y = 58;
    data.sections.forEach((s) => {
      doc.setFontSize(10);
      doc.text(`§${s.code}  ${s.name}  —  ${s.status.toUpperCase()}  ·  last review ${s.last_review}`, 14, y);
      y += 6;
    });
    y += 4;
    doc.setFontSize(12);
    doc.text("Open findings:", 14, y);
    y += 8;
    data.findings.forEach((f) => {
      doc.setFontSize(9);
      doc.text(`${f.id}  ${f.title}  ·  owner ${f.owner}  ·  due ${f.due}  ·  ${f.status}`, 14, y);
      y += 6;
    });
    doc.save("examination-evidence-package.pdf");
  };

  return (
    <div>
      <PageHeader
        title="Examination Readiness"
        subtitle={`Composite score ${data.score}/100 · ${data.days_to_next_review} days to next internal review`}
        sections={data.sections.map(s => s.code)}
        right={<button data-testid="generate-evidence-pdf" onClick={generatePDF} className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-sm">Generate examination evidence package</button>}
      />
      <div className="p-6 grid grid-cols-3 gap-3">
        <Card className="col-span-1">
          <CardHeader title="Readiness score" />
          <div className="p-6 text-center">
            <div className="text-[64px] font-medium tabular text-navy">{data.score}</div>
            <div className="text-[12px] text-ink-subtle">of 100</div>
            <div className="text-[11px] text-risk-low mt-3 font-medium">▲ 3% vs last review</div>
            <div className="text-[11px] text-ink-muted mt-3">Maintained above the bank's internal target of 90.</div>
          </div>
        </Card>
        <Card className="col-span-2">
          <CardHeader title="Section-by-section coverage" subtitle="CBN Baseline Standards for Automated AML Solutions" />
          <div className="p-3 grid grid-cols-3 gap-2">
            {data.sections.map((s) => (
              <div key={s.code} className="border border-line rounded-sm p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] font-medium">{s.code}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.status === 'green' ? 'bg-risk-low' : s.status === 'amber' ? 'bg-risk-medium' : 'bg-risk-high'}`} />
                </div>
                <div className="text-[11px] font-medium mb-1">{s.name}</div>
                <div className="text-[10px] text-ink-subtle">Last review {s.last_review}</div>
                <button className="text-[10px] text-amber-600 mt-1">View evidence →</button>
              </div>
            ))}
          </div>
        </Card>
        <Card className="col-span-3">
          <CardHeader title="Open findings register" />
          <table className="dense w-full">
            <thead><tr><th>ID</th><th>Title</th><th>Owner</th><th>Severity</th><th>Section</th><th>Due</th><th>Age</th><th>Status</th></tr></thead>
            <tbody>
              {data.findings.map(f => (
                <tr key={f.id}><td className="hash-mono text-[11px]">{f.id}</td><td className="text-[12px]">{f.title}</td><td className="text-[12px]">{f.owner}</td><td className="text-[11px]">{f.severity}</td><td className="text-[11px]">{f.section}</td><td className="text-[11px]">{f.due}</td><td className="text-[11px]">{f.age_days}d</td><td><StatusPill status={f.status} /></td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
